import { create } from 'zustand';
import { SelectOption } from '../interfaces/SelectOption';
import { SelectDateFilterProps } from '../interfaces/Pipeline';
import { ColumnDef, OnChangeFn, Updater, VisibilityState } from '@tanstack/react-table';

type DateRangeValue = {
  startDate: Date | string;
  endDate: Date | string;
}

type MasterDateRange = {
  pipeline?: DateRangeValue | null;
  merchant?: DateRangeValue | null;
  merchants?: DateRangeValue | null;
  invoices?: DateRangeValue | null;
}

type DataTableState<T = any> = {
  tablesData: { [key: string]: any[] };
  filteredTablesData: { [key: string]: any[] };
  selectedFilters: { [key: string]: { [key: string]: string } };
  selectedColumns: { [key: string]: SelectOption[] };
  filterTextTable: { [key: string]: string };
  isLoadingTable: { [key: string]: boolean };
  isValidatingTable: { [key: string]: boolean };
  isFilterDate: boolean;
  masterDateRange: MasterDateRange;
  typeDateFilter: string;
  selectDateFilter: SelectDateFilterProps;
  isFullyLoaded: boolean;
  allDataPipeline: any[];
  fetchCounter: number;
  tableColumnVisibility: Record<string, Record<string, boolean>>;
  tableColumns: { [key: string]: ColumnDef<T>[] };
  setFetchCounter: (value: number) => void;
  setTablesData: (tableKey: string, data: any[]) => void;
  setFilteredTablesData: (tableKey: string, filteredData: any[]) => void;
  setSelectedFilter: (tableKey: string, filterType: string, value: string[] | [Date, Date]) => void;
  setFilterTextTable: (tableKey: string, filterTextTable: string) => void;
  setSelectColumn: (tableKey: string, value: SelectOption[]) => void;
  setIsLoadingTable: (tableKey: string, value: boolean) => void;
  setIsValidatingTable: (tableKey: string, value: boolean) => void;
  setIsFilterDate: (value: boolean) => void;
  setMasterDateRange: (value: MasterDateRange) => void;
  setTypeDateFilter: (value: string) => void;
  setSelectDateFilter: (value: SelectDateFilterProps) => void;
  setIsFullyLoaded: (value: boolean) => void;
  setAllDataPipeline: (value: any[]) => void;
  setTableColumnVisibility: (tableKey: string, updater: Updater<VisibilityState>) => void;
  setTableColumns: (tableKey: string, value: ColumnDef<T>[]) => void;
};

export const useDataTableStore = create<DataTableState>((set) => ({
  selectDateFilter: {} as SelectDateFilterProps,
  typeDateFilter: '1',
  masterDateRange: {
    pipeline: null,
    merchant: null,
    invoices: null,
  },
  isFilterDate: false,
  tablesData: {},
  filteredTablesData: {},
  selectedFilters: {},
  filterTextTable: {},
  selectedColumns: {},
  isLoadingTable: {},
  isValidatingTable: {},
  isFullyLoaded: false,
  allDataPipeline: [],
  fetchCounter: 0,
  tableColumnVisibility: {},
  tableColumns: {},
  setSelectDateFilter: (value) => set({ selectDateFilter: value }),
  setMasterDateRange: (value) => set({ masterDateRange: value }),
  setTypeDateFilter: (value) => set({ typeDateFilter: value }),
  setFetchCounter: (value) => set({ fetchCounter: value }),
  setAllDataPipeline: (value) => set({ allDataPipeline: value }),
  setIsFullyLoaded: (value) => set({ isFullyLoaded: value }),
  setIsFilterDate: (value) => set({ isFilterDate: value }),
  setIsLoadingTable: (tableKey, value) => {
    set((state) => ({
      isLoadingTable: {
        ...state.isLoadingTable,
        [tableKey]: value,
      },
    }));
  },
  setIsValidatingTable: (tableKey, value) => {
    set((state) => ({
      isValidatingTable: {
        ...state.isValidatingTable,
        [tableKey]: value,
      },
    }));
  },
  setTablesData: (tableKey, data) => {
    set((state) => ({
      tablesData: {
        ...state.tablesData,
        [tableKey]: data,
      },
    }));
  },
  setFilteredTablesData: (tableKey, filteredData) => {
    set((state) => ({
      filteredTablesData: {
        ...state.filteredTablesData,
        [tableKey]: filteredData,
      },
    }));
  },
  setSelectedFilter: (tableKey: string, filterType: string, value: string[] | [Date, Date]) => {
    set(
      (state) =>
      ({
        selectedFilters: {
          ...state.selectedFilters,
          [tableKey]: {
            ...state.selectedFilters[tableKey],
            [filterType]: value,
          },
        },
      } as DataTableState)
    );
  },

  setFilterTextTable: (tableKey, filterTextTable) => {
    set((state) => ({
      filterTextTable: {
        ...state.filterTextTable,
        [tableKey]: filterTextTable,
      },
    }));
  },

  setSelectColumn: (tableKey, filteredData) => {
    set((state) => ({
      selectedColumns: {
        ...state.selectedColumns,
        [tableKey]: filteredData,
      },
    }));
  },

  setTableColumnVisibility: (tableKey, updater) =>
    set((state) => {
      const prev = state.tableColumnVisibility[tableKey] || {};
      const newValue = typeof updater === 'function' ? updater(prev) : updater;

      return {
        tableColumnVisibility: {
          ...state.tableColumnVisibility,
          [tableKey]: newValue,
        },
      };
    }),
  setTableColumns: (tableKey, filteredData) => {
    set((state) => ({
      tableColumns: {
        ...state.tableColumns,
        [tableKey]: filteredData,
      },
    }));
  }
}));
